/**
 * Issue #601 — correcting a field or a net IN PLACE: its name, what it is
 * (`kind`), and what it is made of (`surface`).
 *
 * THE GAP THIS CLOSES, and why it is a data-quality question rather than a
 * convenience one. A surface could be added (#542), re-rated (#166 FAC-09),
 * pinned (#548) and archived (FAC-11) from the client, but never RENAMED —
 * the PATCH has accepted `name`/`kind`/`surface` since #542 and the only key
 * any client ever sent was `condition` (`lib/conditionUpdate.js`). So the
 * only way to fix "Net 2" that is really "Net 3" was to add a new row and
 * archive the old one, which throws away the facility's id and with it its
 * #211 ratings, its `created_at`, and every training session pointing at it
 * (`training_sessions.facility_id`). Martin is importing ~97 OSM-sourced
 * grounds (#580) and will correct the Auckland names against the council
 * park PDFs afterwards; add-and-archive would silently shred the history of
 * every surface he touched.
 *
 * THE RULES LIVE HERE, not in the component, for `lib/facilityAdd.js`'s
 * reason verbatim: this repo mounts no components in unit tests, so a rule
 * written inside JSX is a rule nothing can assert.
 *
 * --- IT IS A DIFF, LIKE `groundEditDiff` AND UNLIKE THE RE-RATE ---
 *
 * Only what CHANGED is sent. `lib/conditionUpdate.js` deliberately inverts
 * that rule for the rating (a rating goes stale by standing still, so
 * re-confirming it is real news the server re-dates); none of these three
 * fields ages. A name is a name until somebody corrects it, so re-sending an
 * untouched one would stamp `updated_by` and the FAC-10 ledger with an edit
 * nobody made — and #500's ledger is only worth reading if every line in it
 * happened.
 *
 * --- KIND MAY BE UNANSWERED, AND UNANSWERED IS NOT AN ANSWER ---
 *
 * `ground_facilities.kind` is nullable and pre-#544 rows were deliberately
 * NOT backfilled: null means nobody has said, not "field". The API's patch
 * schema says the same thing from the other side — `kind` is not
 * `.nullable()` there, so "a correction can say what a pre-#544 unknown row
 * is, but cannot un-know one" (#544). Two consequences, both deliberate:
 *
 *   - an unanswered kind is ABSENT from the patch, never `null`;
 *   - and it does not block the save, so a legacy row can still be renamed
 *     by somebody who does not know whether it is a field or a net. That is
 *     where this differs from `groundEditErrors`, which requires
 *     `place_type` — every ground HAS one (NOT NULL), so requiring it costs
 *     nothing; requiring `kind` would make the legacy rows this ticket
 *     exists to fix the only ones it could not fix.
 *
 * --- WHO MAY: the parent ground's gate, and NO NEW ALIAS FOR IT ---
 *
 * A facility has no gate of its own — the PATCH sits behind
 * `checkGroundCorrectionGate` on the PARENT (`api/src/routes/groundGate.ts`),
 * exactly as the create, the re-rate and the archive do. This module
 * therefore exports no predicate of its own: `canAddFacility`
 * (`lib/facilityAdd.js`) and `canUpdateCondition` (`lib/conditionUpdate.js`)
 * are already `canOfferGroundEdit` by delegation, and a fourth alias for one
 * server gate is a fourth thing to keep in step. The hosts gate this control
 * with the mirror they already hold:
 *
 *   - `GroundsMap`'s facility list gates the whole row — add, re-rate,
 *     archive, and now correct — on one `canEdit`, which is
 *     `canUpdateCondition(selected, user)`;
 *   - `/admin/venues` renders it unconditionally, as it does the ground
 *     correction form beside it: that page is behind the router's admin gate
 *     and `checkGroundCorrectionGate` admits an admin on any standing record.
 *
 * Like every client mirror of that gate, it is the SUBSET the client can
 * evaluate honestly; the server also refuses when other players rely on the
 * ground, and that refusal comes back as prose written to be shown.
 */

import { FACILITY_KINDS, FACILITY_SURFACES } from "./facilities";

/** The three fields this correction covers, in the form's order. The rest of
 * the patch schema is owned by the gestures that already exist: `condition`
 * by `ConditionUpdate`, the pin by `FacilityAddForm`/`facilityPin`, and
 * `is_indoor`/`has_lighting` by nothing yet (out of scope, #601). */
export const EDITABLE_FACILITY_FIELDS = ["name", "kind", "surface"];

/** `facilityCreateSchema`/`facilityPatchSchema`'s `max(120)`, mirrored so a
 * paste that is too long is answered here rather than as a 400. */
export const FACILITY_NAME_MAX = 120;

/**
 * Where the correction goes: `(ground_id, facility_id)`, the only address
 * the API has for a facility (a facility id must not be able to answer
 * "which ground is this attached to"). `null` when either half is missing,
 * so a caller cannot PATCH `/grounds/undefined/facilities/undefined`.
 *
 * NOT `conditionUpdatePath`, whose facility argument is optional BY DESIGN
 * — it addresses the venue when there is no facility, which for this form
 * would silently rename the ground instead of the net.
 */
export function facilityEditPath(groundId, facilityId) {
  if (!groundId || !facilityId) return null;
  return `/grounds/${groundId}/facilities/${facilityId}`;
}

/**
 * Form state seeded from the row. A value outside the shipped vocabulary
 * reads as UNANSWERED rather than being carried through: whatever it is, it
 * is not something this form may send back (`facilitySurfaceGlyph`'s
 * argument — a surface the client cannot read is not turf).
 */
export function facilityEditForm(facility) {
  return {
    name: String(facility?.name ?? ""),
    kind: FACILITY_KINDS.includes(facility?.kind) ? facility.kind : null,
    surface: FACILITY_SURFACES.includes(facility?.surface) ? facility.surface : null,
  };
}

/**
 * Client-side validation mirroring the patch schema, so the ordinary typo is
 * answered instantly instead of by a round trip. The server still rejects
 * the same things; this is not the boundary.
 *
 * An unanswered kind or surface is NOT an error — see the header: it is
 * "I did not touch this", and the diff drops it.
 */
export function facilityEditErrors(form) {
  const errors = {};
  const name = String(form?.name ?? "").trim();
  if (!name) errors.name = "Required";
  else if (name.length > FACILITY_NAME_MAX) errors.name = `Use ${FACILITY_NAME_MAX} characters or fewer`;
  if (form?.kind != null && !FACILITY_KINDS.includes(form.kind)) errors.kind = "Choose field or net";
  if (form?.surface != null && !FACILITY_SURFACES.includes(form.surface)) errors.surface = "Choose a surface";
  return errors;
}

/**
 * The minimal patch: only what the user actually changed, keyed exactly as
 * `facilityPatchSchema` names its fields.
 *
 * The name is compared TRIMMED against the trimmed stored value, because the
 * schema trims it too — adding a trailing space is not a correction, and
 * sending it as one would put a no-op line in the ledger.
 */
export function facilityEditDiff(facility, form) {
  const patch = {};
  const name = String(form?.name ?? "").trim();
  if (name && name.length <= FACILITY_NAME_MAX && name !== String(facility?.name ?? "").trim()) {
    patch.name = name;
  }
  if (FACILITY_KINDS.includes(form?.kind) && form.kind !== facility?.kind) patch.kind = form.kind;
  if (FACILITY_SURFACES.includes(form?.surface) && form.surface !== facility?.surface) patch.surface = form.surface;
  return patch;
}

/** Is Save worth pressing? One question, asked of the one builder, so the
 * button and the request can never disagree about what "changed" means.
 * An empty patch is also what the API answers 400 to ("at least one field"). */
export function facilityEditChanges(facility, form) {
  return Object.keys(facilityEditDiff(facility, form)).length > 0;
}
